/**
 * Racing outcome cell: runner icon(s) + optional odds.
 * Used by schedule results tab for racing sports.
 */
import React from 'react';
import { printOdd } from 'shared/utils/common';
import OutcomeImages from 'shared/components/outcomes/OutcomeImages';

function normalizeRacingRaw(raw) {
  if (raw == null) return [];
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr.map((x) => {
    if (x != null && typeof x === 'object' && ('result' in x || 'code' in x || 'odd' in x)) {
      return {
        result: x.result != null ? String(x.result) : '',
        code: x.code != null ? String(x.code) : null,
        odd: x.odd != null ? Number(x.odd) : null,
      };
    }
    return { result: String(x), code: null, odd: null };
  });
}

const PLACE_GROUP = 2;   // market_type_id % 100 === 2 (1002, 2002, 3002)
const SHOW_GROUP = 3;    // market_type_id % 100 === 3 (1003, 2003, 3003)
const COMBO_FORECAST_GROUP = 5;  // 1005, 2005
const COMBO_TRICAST_GROUP = 7;   // 1007, 2007

/**
 * Render outcome as runner icon(s) and odd. Uses outcome code when available (fixes forecast/tricast icons).
 * Place: two lines (one runner + odd per line). Show: three lines. Combination forecast/tricast: one outcome, one odd only.
 * showOdds: when false (e.g. mobile summary), only outcome is shown.
 */
export default function RacingOutcomeCell({ raw, n_runners, sportId, oddsFormat = 'decimal', marketTypeId, showOdds = true }) {
  let items = normalizeRacingRaw(raw);
  if (items.length === 0) return <span className="text-muted-foreground">—</span>;

  const group = Number.isFinite(marketTypeId) ? marketTypeId % 100 : null;
  if (group === COMBO_FORECAST_GROUP || group === COMBO_TRICAST_GROUP) {
    items = items.slice(0, 1);
  }

  const multiLine = group === PLACE_GROUP || group === SHOW_GROUP;
  const wrapperClass = multiLine
    ? 'flex flex-col gap-1'
    : 'inline-flex flex-wrap items-center gap-x-2 gap-y-1';

  return (
    <span className={wrapperClass}>
      {items.map((item, i) => {
        const outcomeForIcons = item.code ?? item.result;
        if (!outcomeForIcons) return null;
        return (
          <span key={i} className="inline-flex items-center gap-1.5">
            <span className="inline-flex items-center gap-0.5">
              <OutcomeImages
                outcome={outcomeForIcons}
                n_runners={n_runners}
                sport_id={sportId}
                br={0}
              />
            </span>
            {showOdds && item.odd != null && (
              <span className="text-muted-foreground text-xs tabular-nums font-medium">
                {printOdd(item.odd, oddsFormat)}
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}
